import { useEffect, useRef } from "react";


export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    setTimeout(() => el.classList.add("opacity-100", "translate-y-0"), 100);
  }, []);

  const handleAnchor = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#0A1628]"
    >
      {/* Background image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20electronic%20circuit%20board%20close%20up%20macro%20photography%2C%20PCB%20with%20capacitors%20and%20chips%2C%20deep%20dark%20navy%20blue%20background%2C%20cyan%20light%20glow%20effects%2C%20tech%20industrial%20atmosphere%2C%20high%20detail%20bokeh%2C%20cinematic%20lighting%2C%20ultra%20sharp%20focus%20on%20surface%20traces%2C%20cutting%20edge%20technology%20theme&width=1920&height=1080&seq=geruite-hero-bg1&orientation=landscape"
          alt="电子元器件科技背景"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/90 via-[#0A1628]/70 to-[#0A1628]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/80 via-transparent to-[#0A1628]/30" />
      </div>

      {/* Animated particles/grid lines */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="w-full h-full"
          style={{
            backgroundImage:
              "linear-gradient(rgba(14,165,233,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(14,165,233,0.3) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text */}
          <div
            ref={heroRef}
            className="opacity-0 translate-y-8 transition-all duration-1000 ease-out"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#0EA5E9]/40 bg-[#0EA5E9]/10 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#0EA5E9] animate-pulse" />
              <span className="text-[#64B5F6] text-xs tracking-wider font-medium">
                专业电子元器件供应商 · 厦门格睿特电子
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              精密元器件
              <br />
              <span className="text-[#0EA5E9]">驱动科技创新</span>
              <br />
              赋能智能制造
            </h1>

            <p className="text-gray-400 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
              深耕电子元器件领域，提供电容器、电感器、半导体、光电器件等
              <strong className="text-gray-300">全品类解决方案</strong>，
              覆盖七大产品系列，为您的产品研发与量产提供可靠供应链保障。
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => handleAnchor("#products")}
                className="flex items-center gap-2 px-6 py-3 bg-[#0EA5E9] text-white text-sm font-semibold rounded-lg hover:bg-[#0284C7] transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line" />
                产品中心
                <i className="ri-arrow-right-line" />
              </button>
              <button
                onClick={() => handleAnchor("#contact")}
                className="flex items-center gap-2 px-6 py-3 border border-white/30 text-white text-sm font-semibold rounded-lg hover:border-white hover:bg-white/10 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-customer-service-line" />
                技术支持
              </button>
            </div>

          </div>

          {/* Right: Visual cards */}
          <div className="hidden lg:flex flex-col gap-4 items-end">
            {/* Main product image */}
            <div className="relative w-full max-w-md rounded-2xl overflow-hidden border border-white/10">
              <img
                src="https://readdy.ai/api/search-image?query=assorted%20electronic%20components%20capacitors%20inductors%20semiconductors%20diodes%20resistors%20arranged%20on%20white%20background%2C%20product%20catalog%20style%20photography%2C%20professional%20industrial%20components%2C%20clean%20minimal%20background%2C%20sharp%20detail%2C%20commercial%20product%20shot&width=600&height=380&seq=geruite-products-showcase1&orientation=landscape"
                alt="电子元器件产品展示"
                className="w-full h-64 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white text-sm font-semibold">全品类电子元器件</p>
                <p className="text-gray-300 text-xs mt-1">电容 · 电感 · 半导体 · 光电 · 热电偶</p>
              </div>
            </div>

            {/* Info cards row */}
            <div className="flex gap-3 w-full max-w-md">
              <div className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0EA5E9]/20 mb-2">
                  <i className="ri-verified-badge-line text-[#0EA5E9]" />
                </div>
                <p className="text-white text-sm font-semibold">原厂授权</p>
                <p className="text-gray-500 text-xs mt-0.5">正规渠道 品质保障</p>
              </div>
              <div className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0EA5E9]/20 mb-2">
                  <i className="ri-truck-line text-[#0EA5E9]" />
                </div>
                <p className="text-white text-sm font-semibold">快速交付</p>
                <p className="text-gray-500 text-xs mt-0.5">现货库存 即时发货</p>
              </div>
              <div className="flex-1 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0EA5E9]/20 mb-2">
                  <i className="ri-customer-service-2-line text-[#0EA5E9]" />
                </div>
                <p className="text-white text-sm font-semibold">技术支持</p>
                <p className="text-gray-500 text-xs mt-0.5">专业选型 全程服务</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
        <span className="text-gray-500 text-xs">向下滚动</span>
        <i className="ri-arrow-down-line text-gray-500" />
      </div>
    </section>
  );
}
